import useDialog from "./useDialog.tsx";
import styles from "./FullScreenDialog.module.scss";
import MiscellaneousXMedium from "../icons/MiscellaneousXMedium.tsx";
import { clsx } from "clsx";
import { ReactNode } from "react";
import { useIntl } from "react-intl";
import Tooltip from "./Tooltip.tsx";
import ErrorBoundary from "./ErrorBoundary.tsx";
import { createPortal } from "react-dom";

interface FullScreenDialogProps {
  children: ReactNode;
  onRequestClose: () => void;
  closeStyle?: "white";
  className?: string;
  hideCloseButton?: boolean;
}

export default function FullScreenDialog({
  children,
  onRequestClose,
  closeStyle,
  className,
  hideCloseButton = false,
}: FullScreenDialogProps) {
  const intl = useIntl();
  const { dialogRef } = useDialog(onRequestClose);

  return createPortal(
    <div
      ref={dialogRef}
      className={clsx(styles.dialog, className)}
      // These are normally provided by a11y-dialog but there can sometimes
      // be a timing issue leading to auto-closing of dialogs opened from
      // the sidebar on small screens
      role="dialog"
      aria-modal="true"
    >
      {!hideCloseButton && (
        <button
          className={clsx(
            "vst-icon-button-square",
            "vst-icon-button-size-small",
            closeStyle === "white"
              ? "vst-icon-button-variant-white"
              : "vst-icon-button-variant-black",
            styles.close
          )}
          data-a11y-dialog-hide="true"
          data-tooltip-content={intl.formatMessage({ defaultMessage: "Close" })}
          data-tooltip-place="left"
          aria-label={intl.formatMessage({ defaultMessage: "Close" })}
        >
          <MiscellaneousXMedium />
        </button>
      )}
      <Tooltip anchorSelect="button,a" />
      <ErrorBoundary
        className={clsx(closeStyle === "white" && styles.whiteError)}
      >
        {children}
      </ErrorBoundary>
    </div>,
    document.body
  );
}
